import type { AxiosRequestConfig } from 'axios'
import type { DeviceType } from '~/types/device-type'
import type { IProject, IReport, IReportedComment, IReportedPost } from '~/types/report'

import config from '~/config'
import { paramsSerializer } from '~/utils/api'
import { request } from '~/utils/request'

export const reportsFiltersKeyName = (key: string): string => {
  switch (key) {
    case 'types':
      return 'entity-type'
    case 'reasons':
      return 'reason-type'
    case 'reviewers':
      return 'profile-id'
    case 'statuses':
      return 'status'
    case 'appKey':
      return 'app-key'
    case 'deviceType':
      return 'device-type'
    default:
      return key
  }
}

/** Reports */

export const getReportsByEntities = (params?: AxiosRequestConfig['params']): Promise<IPaginatedResponse<IReport>> => {
  return request({
    url: `${config.ADMIN_REPORTS_API}/admin/reports-by-entity?${paramsSerializer(params, reportsFiltersKeyName)}`,
  })
}

export const getReportByEntity = (deviceType?: string, appKey?: string, entityId?: string): Promise<IReport> => {
  return request({
    url: `${config.ADMIN_REPORTS_API}/admin/reports-by-entity/device-type/${deviceType}/app-key/${appKey}/entity-id/${entityId}`,
  })
}

export const processBatchReport = (data: {
  appKey: string
  decision: 'delete' | 'no-action'
  deviceType: string
  reportIds: string[]
}): Promise<void> => {
  return request({
    data: {
      decision: data.decision,
      reported_entity_ids: data.reportIds,
    },
    method: 'POST',
    url: `${config.ADMIN_REPORTS_API}/admin/reports/batch-process?app-key=${data.appKey}&device-type=${data.deviceType}`,
  })
}

export const searchComments = ({
  appKey,
  deviceType,
  postId,
  profileId,
}: {
  appKey: string
  deviceType: DeviceType
  postId?: string
  profileId?: string
}): Promise<IReportedComment[]> => {
  const queryParams = []

  if (postId) queryParams.push(`post_id=${postId}`)
  if (profileId) queryParams.push(`profile_id=${profileId}`)

  return request({
    url: `${config.ADMIN_REPORTS_API}/admin/device-type/${deviceType}/app-key/${appKey}/comments${
      queryParams.length ? `?${queryParams.join('&')}` : ''
    }`,
  })
}

export const searchPosts = ({
  appKey,
  deviceType,
  profileId,
}: {
  appKey: string
  deviceType: DeviceType
  profileId?: string
}): Promise<IReportedPost[]> => {
  const queryParams = []

  if (profileId) queryParams.push(`profile_id=${profileId}`)

  return request({
    url: `${config.ADMIN_REPORTS_API}/admin/device-type/${deviceType}/app-key/${appKey}/posts${
      queryParams.length ? `?${queryParams.join('&')}` : ''
    }`,
  })
}

export const searchProjects = ({
  appKey,
  deviceType,
  profileId,
}: {
  appKey: string
  deviceType: DeviceType
  profileId?: string
}): Promise<IProject[]> => {
  const queryParams = []

  if (profileId) queryParams.push(`profile_id=${profileId}`)

  return request({
    url: `${config.ADMIN_REPORTS_API}/admin/device-type/${deviceType}/app-key/${appKey}/projects${
      queryParams.length ? `?${queryParams.join('&')}` : ''
    }`,
  })
}
